/**
 * Instance rows — the pure rules behind the EIA instance table.
 *
 * Ported from legacy ManageAccount.vue (addInstanceClick, :702-743) and
 * InstancesListTable.vue (getErrorMessage, formatFieldValue, error-first sort).
 * Pure functions so each rule is testable without rendering.
 */
import { FIELD, PRICING_MODEL_OPTIONS, requiredInstanceFields } from './portfolioFormRules';

let rowSeq = 0;

/**
 * A stable id for a table row. Rows have no id of their own (uuid is optional
 * and user-typed), and keying by position breaks selection after a delete or
 * the error-first re-sort — legacy hit exactly that (InstancesListTable.vue:303).
 * Stripped before anything is sent to the server.
 */
export function newRowId() {
  rowSeq += 1;
  return `row-${rowSeq}`;
}

/** `Remarks` as a list, whatever the server sent (legacy sometimes falls back to {}). */
function remarksOf(row) {
  return Array.isArray(row?.Remarks) ? row.Remarks : [];
}

/**
 * The table row for an instance the server has validated: what was sent, with
 * the server's version laid over it — as legacy does for EIA (:721-742):
 * pricingModel kept from the form, uavg / u95 as numbers, never saved yet.
 */
export function toTableRow(sent, validated) {
  return {
    ...sent,
    ...validated,
    [FIELD.PRICING_MODEL]: sent[FIELD.PRICING_MODEL],
    [FIELD.UAVG]: Number(validated?.uavg ?? sent[FIELD.UAVG] ?? 0),
    [FIELD.U95]: Number(validated?.u95 ?? sent[FIELD.U95] ?? 0),
    Remarks: remarksOf(validated),
    isSaved: false,
    adjusted: true,
    _rowId: newRowId(),
  };
}

/**
 * A row from an uploaded file (file-upload/validate `Data`) as a table row —
 * the fields legacy keeps for EIA (UploadDownloadTemplate.vue onFileChanged,
 * :629-655): uavg / u95 as numbers, OCPU / Memory only for OCI, never saved yet.
 */
export function toUploadedRow(instance, provider) {
  const oci = String(provider ?? '').toUpperCase() === 'OCI';
  return {
    uuid: instance?.uuid?.toString(),
    cloud_csp: instance?.cloud_csp,
    region: instance?.region,
    hostname: instance?.hostname,
    kernel_version: instance?.kernel_version,
    [FIELD.INSTANCE_TYPE]: instance?.[FIELD.INSTANCE_TYPE],
    [FIELD.MAX_CPU]: instance?.[FIELD.MAX_CPU],
    [FIELD.MAX_MEM]: instance?.[FIELD.MAX_MEM],
    [FIELD.MAX_NETWORK_BW]: instance?.[FIELD.MAX_NETWORK_BW],
    [FIELD.MAX_DISK_BW]: instance?.[FIELD.MAX_DISK_BW],
    [FIELD.MAX_IOPS]: instance?.[FIELD.MAX_IOPS],
    os: instance?.os,
    Remarks: remarksOf(instance),
    total_memory: instance?.total_memory,
    availability_zone: instance?.availability_zone,
    isSaved: false,
    adjusted: instance?.adjusted || true,
    [FIELD.PRICING_MODEL]: instance?.pricingModel,
    [FIELD.UAVG]: Number(instance?.uavg ?? 0),
    [FIELD.U95]: Number(instance?.u95 ?? 0),
    [FIELD.OS]: instance?.os_type,
    ...(oci
      ? { [FIELD.OCPU]: instance?.ocpu?.toString(), [FIELD.MEMORY]: instance?.memory?.toString() }
      : {}),
    _rowId: newRowId(),
  };
}

/**
 * A row from the self-perf file (file-upload/validate `UDF`) — kept as the
 * server sent it (legacy spreads it), never saved yet, plus the table's id.
 */
export function toSelfPerfRow(row) {
  return { ...row, isSaved: false, _rowId: newRowId() };
}

/**
 * A row of a SAVED portfolio (/get-portfolio `input_data` or `udf`). It comes
 * back in the shape Save sent it (toApiRow), so it is kept as is — marked
 * saved, as legacy getPortfolioInstances does, plus the table's id.
 */
export function toSavedRow(row) {
  return { ...row, isSaved: true, _rowId: newRowId() };
}

/** The server's message for one cell, or '' (legacy getErrorMessage). */
export function remarkFor(row, field) {
  return remarksOf(row).find((r) => r?.Field === field)?.Message ?? '';
}

/**
 * Errors vs warnings across all rows (legacy getTotalErrorsAndWarnings): a
 * remark mentioning "unsupported" is a warning; anything else is an error —
 * and any error blocks Save.
 */
export function countRemarks(rows) {
  return rows.reduce(
    (counts, row) => {
      for (const remark of remarksOf(row)) {
        if (isWarning(remark?.Message)) counts.warnings += 1;
        else counts.errors += 1;
      }
      return counts;
    },
    { errors: 0, warnings: 0 },
  );
}

/**
 * Does this row carry at least one ERROR (not just an "unsupported" warning)?
 * The same test countRemarks uses — legacy's Delete Error Records used a
 * slightly different one (a remark with no Message counted as an error but did
 * not get its row deleted), so after a delete "Errors" could stay above 0.
 */
export function hasErrorRemark(row) {
  return remarksOf(row).some((remark) => !isWarning(remark?.Message));
}

/**
 * Delete Error Records (legacy BulkDelete.vue :166-210): the rows that stay,
 * and how many went. Warning-only rows stay.
 */
export function withoutErrorRows(rows) {
  const kept = rows.filter((row) => !hasErrorRemark(row));
  return { kept, removed: rows.length - kept.length };
}

/** Anything not saved yet? (legacy hasUnsavedChanges — any row with !isSaved) */
export function hasUnsavedRows(rows) {
  return rows.some((row) => !row?.isSaved);
}

/** Does this row carry any problem? */
export function hasRemarks(row) {
  return remarksOf(row).length > 0;
}

/**
 * "unsupported" remarks are shown in orange, not red — a warning, not an error
 * (legacy getErrorClass).
 */
export function isWarning(message) {
  return /unsupported/i.test(message ?? '');
}

/**
 * Rows with problems first, the rest in their original order — legacy re-sorts
 * the list the same way so issues are seen without paging.
 */
export function sortProblemsFirst(rows) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => Number(hasRemarks(b.row)) - Number(hasRemarks(a.row)) || a.index - b.index)
    .map(({ row }) => row);
}

const PRICING_LABELS = Object.fromEntries(PRICING_MODEL_OPTIONS.map((o) => [o.value, o.label]));

/** A cell value as legacy displays it (formatFieldValue). */
export function formatCell(value, field) {
  if (value === null || value === undefined || value === '') return '—';
  if (field === FIELD.OS) return String(value).charAt(0).toUpperCase() + String(value).slice(1);
  if (field === FIELD.PRICING_MODEL) return PRICING_LABELS[value] ?? value;
  return String(value);
}

/** The row as the server should see it: without the table's own bookkeeping. */
export function toApiRow(row) {
  const rest = { ...row };
  delete rest._rowId;
  return rest;
}

/* --- editing a cell (legacy InstancesListTable.vue) ----------------------- */

/** The edit kind per field: a dropdown or a number box. UUID is not editable. */
export const EDITABLE_FIELDS = Object.freeze({
  [FIELD.REGION]: 'select',
  [FIELD.INSTANCE_TYPE]: 'select',
  [FIELD.OS]: 'select',
  [FIELD.PRICING_MODEL]: 'select',
  [FIELD.MAX_CPU]: 'number',
  [FIELD.MAX_MEM]: 'number',
  [FIELD.MAX_DISK_BW]: 'number',
  [FIELD.MAX_NETWORK_BW]: 'number',
  [FIELD.MAX_IOPS]: 'number',
  [FIELD.OCPU]: 'number',
  [FIELD.MEMORY]: 'number',
  [FIELD.UAVG]: 'number',
  [FIELD.U95]: 'number',
});

function blank(value) {
  return value === null || value === undefined || value === '';
}

function isFlexType(instanceType) {
  return /flex/i.test(String(instanceType ?? ''));
}

/**
 * The row after one cell changes. On OCI, a switch to a non-flex type — or
 * from non-flex to flex — clears OCPU / Memory so stale values are never sent
 * (legacy handleInstanceChange).
 */
export function applyEdit(row, field, value, provider) {
  const next = { ...row, [field]: value };
  const oci = String(provider ?? '').toUpperCase() === 'OCI';
  if (oci && field === FIELD.INSTANCE_TYPE) {
    if (!isFlexType(value) || !isFlexType(row[FIELD.INSTANCE_TYPE])) {
      next[FIELD.OCPU] = null;
      next[FIELD.MEMORY] = null;
    }
  }
  return next;
}

/**
 * Fields a table row must have before it is sent to the server — EVERY
 * mandatory field, the same list the Add Instance form requires
 * (requiredInstanceFields: region, instance type, pricing model, the five
 * max-usage figures, plus OCPU / Memory on an OCI flex type).
 *
 * Wider than legacy, which checked only region + instance type here
 * (requiredFieldsFor) and let the server refuse the rest — the user asked
 * that no request go out until every mandatory cell is filled.
 */
export function requiredRowFields(row, provider) {
  return requiredInstanceFields(provider, row[FIELD.INSTANCE_TYPE]);
}

/** Required fields this row leaves empty. */
export function missingRowFields(row, provider) {
  return requiredRowFields(row, provider).filter((field) => blank(row[field]));
}

/**
 * Keep an edit WITHOUT asking the server, flagging the empty required cells
 * "Required" (legacy applyRowLocally) — the server would only refuse the row
 * and the edit would be lost.
 */
export function markRequired(row, provider) {
  const checked = requiredRowFields(row, provider);
  const missing = missingRowFields(row, provider);
  // Only the required cells change: an empty one says "Required", a refilled
  // one loses its old "Required". The server's other remarks stay — dropping
  // a filled cell's "Too high" would hide a real problem until the next check.
  const stale = (r) =>
    missing.includes(r?.Field) || (checked.includes(r?.Field) && r?.Message === 'Required');
  return {
    ...row,
    Remarks: [
      ...remarksOf(row).filter((r) => !stale(r)),
      ...missing.map((field) => ({ Field: field, Message: 'Required' })),
    ],
    isSaved: false,
  };
}

/**
 * An edited row after the server checked it: the server's version laid over
 * the edit, same table id (legacy saveEdit, :1314-1348).
 */
export function mergeRevalidated(edited, validated) {
  return {
    ...edited,
    ...validated,
    [FIELD.UAVG]: Number(validated?.uavg ?? edited[FIELD.UAVG] ?? 0),
    [FIELD.U95]: Number(validated?.u95 ?? edited[FIELD.U95] ?? 0),
    Remarks: remarksOf(validated),
    isSaved: false,
    adjusted: true,
    _rowId: edited._rowId,
  };
}
